import {
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import { apiRequest, signOut } from "./api";

export type ShellActiveItem = "war-thunder" | "squad" | "contact";

export function Brand() {
  return (
    <a className="brand" href="/" aria-label="CheemsPay 首页">
      <span className="brand-mark">
        <img
          alt=""
          aria-hidden="true"
          height="36"
          src="/brand/cheems-burger.webp"
          width="36"
        />
      </span>
      <span className="brand-name">
        Cheems<span>Pay</span>
      </span>
    </a>
  );
}

export function TopbarAccountLink() {
  return (
    <a className="topbar-cta" href="/account">
      账户
    </a>
  );
}

export function TopbarLoginLink({ returnTo }: { returnTo?: string }) {
  const href = returnTo
    ? `/?access=login&returnTo=${encodeURIComponent(returnTo)}`
    : "/?access=login";
  return (
    <a className="topbar-cta" href={href}>
      登录
    </a>
  );
}

export function SignOutControl() {
  return (
    <button
      className="topbar-quiet"
      type="button"
      onClick={() => {
        void signOut().then(() => window.location.assign("/"));
      }}
    >
      退出
    </button>
  );
}

export function SessionAction({
  signedOutReturnTo,
}: {
  signedOutReturnTo?: string;
}) {
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  useEffect(() => {
    let active = true;
    void apiRequest("/api/session")
      .then(() => {
        if (active) setSignedIn(true);
      })
      .catch(() => {
        if (active) setSignedIn(false);
      });
    return () => {
      active = false;
    };
  }, []);
  if (signedIn === null) {
    return (
      <span className="topbar-cta topbar-cta-pending" aria-hidden="true">
        登录
      </span>
    );
  }
  return signedIn ? (
    <TopbarAccountLink />
  ) : (
    <TopbarLoginLink returnTo={signedOutReturnTo} />
  );
}

export function Shell({
  children,
  action,
  back,
  activeItem,
  subnav,
  mainClassName = "",
}: PropsWithChildren<{
  action?: ReactNode;
  back?: { href: string; label: string };
  activeItem?: ShellActiveItem;
  subnav?: ReactNode;
  mainClassName?: string;
}>) {
  return (
    <div className="site-shell">
      <a className="skip-link" href="#main-content">
        跳到主要内容
      </a>
      <header className="topbar">
        <div className="topbar-start">
          <Brand />
          {back ? (
            <a className="topbar-back" href={back.href}>
              <span aria-hidden="true">←</span>
              {back.label}
            </a>
          ) : null}
        </div>
        <div className="topbar-tools">
          <nav aria-label="主导航" className="primary-nav">
            <a
              aria-current={activeItem === "war-thunder" ? "page" : undefined}
              href="/"
            >
              Bomana
            </a>
            <a
              aria-current={activeItem === "squad" ? "page" : undefined}
              href="/products/squad-tactical-planner"
            >
              战术规划
            </a>
            <a
              aria-current={activeItem === "contact" ? "page" : undefined}
              href="/contact"
            >
              支持
            </a>
          </nav>
          {action}
        </div>
      </header>
      {subnav ? <div className="shell-subnav">{subnav}</div> : null}
      <main className={mainClassName} id="main-content" tabIndex={-1}>
        {children}
      </main>
      <footer>
        <span>CheemsPay</span>
        <span>独立软件的购买、授权与支持</span>
        <a
          className="icp-link"
          href="https://beian.miit.gov.cn/"
          rel="noreferrer"
          target="_blank"
        >
          黑ICP备2025043874号-1
        </a>
      </footer>
    </div>
  );
}

export function Button({
  className = "",
  variant = "primary",
  busy = false,
  busyLabel = "处理中…",
  disabled,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "danger" | "quiet";
  busy?: boolean;
  busyLabel?: string;
}) {
  return (
    <button
      aria-busy={busy || undefined}
      className={`button button-${variant} ${className}`.trim()}
      disabled={disabled || busy}
      {...props}
    >
      {busy ? busyLabel : children}
    </button>
  );
}

export function StatusPill({
  children,
  tone = "neutral",
}: PropsWithChildren<{
  tone?: "good" | "warn" | "danger" | "info" | "neutral";
}>) {
  return <span className={`status-pill ${tone}`}>{children}</span>;
}

export function AsyncStatus({
  children,
  tone = "info",
  action,
  compact = false,
}: PropsWithChildren<{
  tone?: "loading" | "success" | "error" | "warning" | "info";
  action?: ReactNode;
  compact?: boolean;
}>) {
  const isError = tone === "error";
  return (
    <div
      aria-live={isError ? "assertive" : "polite"}
      className={`async-status async-status-${tone}${compact ? " compact" : ""}`}
      role={isError ? "alert" : "status"}
    >
      {tone === "loading" ? (
        <span aria-hidden="true" className="async-status-spinner" />
      ) : null}
      <div className="async-status-copy">{children}</div>
      {action ? <div className="async-status-action">{action}</div> : null}
    </div>
  );
}

export function PageHeader({
  kicker,
  title,
  description,
  actions,
  meta,
}: {
  kicker?: string;
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  meta?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div className="page-header-copy">
        {kicker ? <span className="eyebrow">{kicker}</span> : null}
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
        {meta ? <div className="page-header-meta">{meta}</div> : null}
      </div>
      {actions ? <div className="page-header-actions">{actions}</div> : null}
    </header>
  );
}

export interface SectionNavItem<T extends string> {
  id: T;
  label: string;
  badge?: number | string | null;
  href?: string;
}

export function SectionNav<T extends string>({
  label,
  items,
  activeItem,
  onSelect,
}: {
  label: string;
  items: Array<SectionNavItem<T>>;
  activeItem: T;
  onSelect?: (item: T) => void;
}) {
  return (
    <nav aria-label={label} className="section-nav">
      {items.map((item) => {
        const content = (
          <>
            <span>{item.label}</span>
            {item.badge !== undefined && item.badge !== null ? (
              <small aria-label={`${item.badge} 项`}>{item.badge}</small>
            ) : null}
          </>
        );
        if (item.href) {
          return (
            <a
              aria-current={activeItem === item.id ? "page" : undefined}
              href={item.href}
              key={item.id}
            >
              {content}
            </a>
          );
        }
        return (
          <button
            aria-current={activeItem === item.id ? "page" : undefined}
            key={item.id}
            onClick={() => onSelect?.(item.id)}
            type="button"
          >
            {content}
          </button>
        );
      })}
    </nav>
  );
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  closeLabel = "关闭对话框",
  dismissible = true,
  className = "",
}: PropsWithChildren<{
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  footer?: ReactNode;
  closeLabel?: string;
  dismissible?: boolean;
  className?: string;
}>) {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const dismissibleRef = useRef(dismissible);
  onCloseRef.current = onClose;
  dismissibleRef.current = dismissible;

  useEffect(() => {
    if (!open) {
      return;
    }
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusFrame = window.requestAnimationFrame(() => {
      const firstFocusable = dialogRef.current?.querySelector<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      (firstFocusable ?? dialogRef.current)?.focus();
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && dismissibleRef.current) {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) {
        return;
      }
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (!focusable.length) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
      previousFocusRef.current?.focus();
    };
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="dialog-backdrop"
      onMouseDown={(event) => {
        if (
          dismissible &&
          event.target === event.currentTarget
        ) {
          onClose();
        }
      }}
    >
      <div
        aria-describedby={description ? descriptionId : undefined}
        aria-labelledby={titleId}
        aria-modal="true"
        className={`dialog-panel ${className}`.trim()}
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
      >
        <header className="dialog-header">
          <div>
            <h2 id={titleId}>{title}</h2>
            {description ? <p id={descriptionId}>{description}</p> : null}
          </div>
          {dismissible ? (
            <button
              aria-label={closeLabel}
              className="dialog-close"
              onClick={onClose}
              type="button"
            >
              ×
            </button>
          ) : null}
        </header>
        <div className="dialog-body">{children}</div>
        {footer ? <footer className="dialog-footer">{footer}</footer> : null}
      </div>
    </div>
  );
}

export function Field({
  label,
  hint,
  children,
}: PropsWithChildren<{ label: string; hint?: string }>) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

export function EmptyState({
  title,
  children,
}: PropsWithChildren<{ title: string }>) {
  return (
    <div className="empty-state">
      <span className="empty-icon" aria-hidden="true">
        ◌
      </span>
      <strong>{title}</strong>
      <p>{children}</p>
    </div>
  );
}
